import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Button, Form, Input } from 'antd';
import { TFunction, useTranslation } from 'react-i18next';

import FormItem from 'components/form/FormItem';
import { useAuth } from 'components/AuthProvider';
import { displayErrorMessage } from 'components/message';
import { ModalScrollWrapper, ModalFooterWrapper } from 'components/Modal';
interface IFormValues {
  email: string;
  password: string;
}

const getValidationSchema = (t: TFunction) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t('validation.format'))
      .max(
        120,
        t('validation.maxLength', {
          count: 120,
        }),
      )
      .required(t('validation.required')),
    password: yup
      .string()
      .required(t('validation.required'))
      .min(6, t('validation.format'))
      .max(16, t('validation.format')),
  });

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
  onRegisterClick: () => void;
  onUnconfirmed: (email: string) => void;
  onForgotPassword: () => void;
};

export default function SignInForm(props: Props) {
  const { t } = useTranslation();
  const { login, resendSignUp } = useAuth();
  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<IFormValues>({
    mode: 'onTouched',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const {
    onClose,
    onSuccess,
    onRegisterClick,
    onUnconfirmed,
    onForgotPassword,
  } = props;
  const handleError = (error: any) => {
    displayErrorMessage({
      error,
      containerSelector: '.ant-modal',
    });
  };
  const onSubmit = async (values: IFormValues) => {
    try {
      await login(values);

      onSuccess?.();
    } catch (error: any) {
      if (error?.code === 'UserNotConfirmedException') {
        try {
          await resendSignUp({ email: values.email });
          onUnconfirmed(values.email);
        } catch (err) {
          handleError(err);
        }
      } else {
        handleError(error);
      }
    }
  };

  return (
    <>
      <Form onFinish={handleSubmit(onSubmit)} layout="vertical">
        <ModalScrollWrapper>
          <div className="modal__subtitle">
            {t('SignInForm.NoAccount')} &nbsp;
            <Button
              className="custom-link"
              id="signIn-register-btn"
              type="link"
              onClick={onRegisterClick}
            >
              {t('SignInForm.Register')}
            </Button>
          </div>
          <FormItem
            name="email"
            control={control}
            render={({ field }) => (
              <Input
                data-test-id="signIn-email-input"
                placeholder={t('SignInForm.Email')}
                size="large"
                {...field}
              />
            )}
          />
          <FormItem
            name="password"
            control={control}
            render={({ field }) => (
              <Input.Password
                data-test-id="signIn-password-input"
                placeholder={t('SignInForm.Password')}
                size="large"
                {...field}
              />
            )}
          />
          <div className="modal__subtitle">
            <Button
              data-test-id="signIn-forgot-password-btn"
              className="custom-link"
              type="link"
              onClick={() => {
                onForgotPassword();
              }}
            >
              {t('SignInForm.ForgotPassword')}
            </Button>
          </div>
        </ModalScrollWrapper>

        <ModalFooterWrapper>
          <div>
            <Button
              onClick={onClose}
              type="link"
              className="custom-link"
              size="large"
              data-test-id="signIn-cancel-btn"
            >
              {t('SignInForm.Cancel')}
            </Button>
          </div>
          <div>
            <Button
              type="primary"
              loading={isSubmitting}
              htmlType="submit"
              size="large"
              data-test-id="signIn-signIn-btn"
            >
              {t('SignInForm.SignIn')}
            </Button>
          </div>
        </ModalFooterWrapper>
      </Form>
    </>
  );
}
